import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import {
  CommentAuthorType,
  Content,
  ContentType,
  InlineComment,
  PublisherReviewStatus,
  WorkflowState,
} from '@amzn/awscat-pattern-library-service-model';

import { useInlineCommentManager } from '../../contexts/InlineCommentManagerContext';
import {
  listNavigatorInlineTextFilters,
  listPageInlineTextFilters,
} from '../../services/ContentFindingFilters';
import { isOptimisticReply, isThreadRoot } from '../../services/InlineCommentManager';
import {
  commentPathMatchesDocId,
  getNodeIdMappings,
  getNodeIdPosition,
  InlineTextFilter,
} from '../../services/InlineTextOperations';
import { resolvePageForLocation } from '../../services/PageOperations';
import { toScannerDisplayName } from '../../services/ScannerFindings';
import { isCommentResolved } from '../v2-common/utils/commentStateUtils';
import { useContent } from '../v2-hooks/useContent';
import { useFilteredInlineComments } from '../v2-hooks/useFilteredInlineComments';
import { useCurrentSectionId } from '../v2-hooks/useUrlHash';
import { useWorkflow } from '../v2-hooks/useWorkflow';
import { CommentCardItem, ReplyViewModel } from './CommentCardViewModel';

export type { CommentCardItem, ReplyViewModel } from './CommentCardViewModel';
export type CommentFilter = 'open' | 'resolved';
export type SourceFilter = 'all' | 'human' | 'ai';

export interface CommentsPanelViewModel {
  readonly filter: CommentFilter;
  readonly setFilter: (filter: CommentFilter) => void;
  readonly sourceFilter: SourceFilter;
  readonly setSourceFilter: (filter: SourceFilter) => void;
  readonly visibleComments: readonly CommentCardItem[];
  readonly draftState: WorkflowState;
  readonly reviewStatus: PublisherReviewStatus;
  readonly isLoading: boolean;
  /** The comment-load failure to display, or undefined when the load succeeded or is still in flight. */
  readonly loadErrorMessage: string | undefined;
  /** Re-attempt the failed comment load. */
  readonly retryLoad: () => void;
  readonly activeThreadId: string | null;
  /** Toggle a thread's selection: selects and reveals if not active, deselects if already active. */
  readonly toggleThread: (threadId: string) => void;
}

function getThreadId(comment: InlineComment): string {
  return comment.threadId || comment.commentId;
}

function filterForThread(
  comments: readonly InlineComment[],
  threadId: string | null,
): CommentFilter | undefined {
  if (!threadId) return undefined;
  const root = comments.find(
    (comment) => isThreadRoot(comment) && getThreadId(comment) === threadId,
  );
  if (!root) return undefined;
  return isCommentResolved(root.state) ? 'resolved' : 'open';
}

function sourceFilterForThread(
  comments: readonly InlineComment[],
  threadId: string,
): SourceFilter | undefined {
  const root = comments.find(
    (comment) => isThreadRoot(comment) && getThreadId(comment) === threadId,
  );
  if (!root) return undefined;
  return root.authorType === CommentAuthorType.SCANNER ? 'ai' : 'human';
}

function toReplyViewModel(comment: InlineComment): ReplyViewModel {
  return {
    commentId: comment.commentId,
    author: comment.principalName || comment.principalId,
    body: comment.commentData,
    relativeTime: comment.createdAt?.toRelative?.() ?? '',
    isOptimisticReply: isOptimisticReply(comment),
  };
}

function getCommentAuthorDisplayName(comment: InlineComment): string {
  if (comment.authorType === CommentAuthorType.SCANNER) {
    // Map the raw scanner agent id to its user-facing display name, matching the
    // scan findings table. Fall back to principal/Unknown when no scanner name.
    const scannerName =
      comment.scannerMetadata?.scannerName ||
      comment.sourceAttributions?.[0]?.sourceScanner?.scannerName;
    if (scannerName) return toScannerDisplayName(scannerName);

    return comment.principalName || comment.principalId || 'Unknown';
  }

  return comment.principalName || comment.principalId || 'Unknown';
}

function toCardViewModel(root: InlineComment, replies: InlineComment[]): CommentCardItem {
  const isScanner = root.authorType === CommentAuthorType.SCANNER;
  const scannerDisplayName = getCommentAuthorDisplayName(root);

  return {
    commentId: root.commentId,
    threadId: getThreadId(root),
    commentBody: root.commentData,
    quotedText: root.textData,
    scannerDisplayName,
    relativeTime: root.createdAt?.toRelative?.() ?? '',
    isScanner,
    replies: replies.map(toReplyViewModel),
    sectionPath: root.path?.[0] ?? '',
    startOffset: root.startOffset,
    principalId: root.principalId,
    sourceUrl: root.sourceAttributions?.[0]?.citations?.[0],
  };
}

function listLayoutFilters(
  content: Content,
  currentSectionId: string | undefined,
): readonly InlineTextFilter[] {
  const contentType = content.contentMetadata.contentType;
  if (contentType === ContentType.Guide || contentType === ContentType.Strategy) {
    return listPageInlineTextFilters(resolvePageForLocation(content, currentSectionId));
  }

  if (contentType === ContentType.Pattern) {
    return content.contentData.contentSections.flatMap((section) =>
      listNavigatorInlineTextFilters(content, section),
    );
  }

  return [];
}

export function useCommentsPanelViewModel(): CommentsPanelViewModel {
  const manager = useInlineCommentManager();
  const workflow = useWorkflow();
  const content = useContent();

  // When the manager changes (active thread, comment list, or load error), then
  // re-read each field. activeThreadId is shared with the section editors so
  // clicking a highlight in the editor selects the comment in this panel, and vice-versa.
  const subscribe = useCallback(
    (onStoreChange: () => void) => manager.on('changed', onStoreChange),
    [manager],
  );
  const activeThreadId = useSyncExternalStore(subscribe, () => manager.activeThreadId);
  const openedComments = useSyncExternalStore(subscribe, () => manager.openedComments);
  const loadError = useSyncExternalStore(subscribe, () => manager.loadError);
  const [filter, setFilter] = useState<CommentFilter>(
    () => filterForThread(manager.openedComments?.comments ?? [], manager.activeThreadId) ?? 'open',
  );
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');

  // A sidebar reveal is an explicit request to show a thread. Unlike the broad
  // changed event, it also fires when the already-active section marker is
  // clicked again, so restore any filters that currently hide that thread.
  useEffect(
    () =>
      manager.on('revealInSidebarRequested', (threadId) => {
        const comments = manager.openedComments?.comments ?? [];
        const selectedFilter = filterForThread(comments, threadId);
        const selectedSourceFilter = sourceFilterForThread(comments, threadId);
        if (selectedFilter) setFilter(selectedFilter);
        if (selectedSourceFilter) setSourceFilter(selectedSourceFilter);
      }),
    [manager],
  );

  // When a section marker switches the active thread, then show the matching open/resolved tab.
  // Guard on the thread transition so routine comment-list refreshes don't clobber a manual tab.
  const prevThreadIdRef = useRef<string | null>(activeThreadId);
  useEffect(() => {
    if (prevThreadIdRef.current === activeThreadId) return;
    prevThreadIdRef.current = activeThreadId;
    const selectedFilter = filterForThread(openedComments?.comments ?? [], activeThreadId);
    if (selectedFilter) setFilter(selectedFilter);
  }, [activeThreadId, openedComments?.comments]);

  // When a card is clicked, toggle its selection and ask the editor to reveal it.
  const toggleThread = useCallback(
    (threadId: string) => {
      if (activeThreadId === threadId) {
        manager.selectThread(null);
      } else {
        manager.revealInEditor(threadId);
      }
    },
    [activeThreadId, manager],
  );

  // When the user asks to retry, re-attempt the failed load. The manager emits
  // 'changed' when it settles, which re-syncs the state above.
  const retryLoad = useCallback(() => {
    manager.retryLoad().catch(() => undefined);
  }, [manager]);

  // Resolve the current page node — for guides, this is the page the user is
  // viewing (looked up by url-hash section id); for patterns, it's the root.
  const currentSectionId = useCurrentSectionId();
  const isPagedContent =
    content?.contentMetadata.contentType === ContentType.Guide ||
    content?.contentMetadata.contentType === ContentType.Strategy;

  // When content or the current section changes, then rebuild the per-editor
  // filters that place each comment against the rendered layout.
  const layoutFilters = useMemo(
    () => (content ? listLayoutFilters(content, currentSectionId) : []),
    [content, currentSectionId],
  );

  // For paged content, filter the panel to the current page body and sections.
  const inlineFilter: InlineTextFilter | readonly InlineTextFilter[] =
    isPagedContent && layoutFilters.length > 0 ? layoutFilters : { type: 'none' };

  const filteredComments = useFilteredInlineComments(inlineFilter, {
    rootOnly: false,
    excludeResolved: false,
  });

  // When content changes, then rebuild the node-id → editor-position mappings
  // used to order comments within an editor.
  const nodeIdMappings = useMemo(
    () => (content ? getNodeIdMappings(content) : undefined),
    [content],
  );

  const visibleComments = useMemo(() => {
    // For non-paged content, use all comments from the manager directly.
    // For paged content, use the filtered results from useFilteredInlineComments.
    const allComments = isPagedContent ? filteredComments : (openedComments?.comments ?? []);

    // Group replies under their thread root.
    const threadMap = new Map<string, { root: InlineComment; replies: InlineComment[] }>();
    for (const c of allComments) {
      const tid = getThreadId(c);
      if (!threadMap.has(tid)) {
        threadMap.set(tid, { root: c, replies: [] });
      }
      if (isThreadRoot(c)) {
        threadMap.get(tid)!.root = c;
      } else {
        threadMap.get(tid)!.replies.push(c);
      }
    }

    return Array.from(threadMap.values())
      .filter(({ root }) => {
        const resolved = isCommentResolved(root.state);
        if (filter === 'open' && resolved) return false;
        if (filter === 'resolved' && !resolved) return false;
        return true;
      })
      .filter(({ root }) => {
        if (sourceFilter === 'all') return true;
        const isAi = root.authorType === CommentAuthorType.SCANNER;
        return sourceFilter === 'ai' ? isAi : !isAi;
      })
      .map(({ root, replies }) => {
        const layoutOrder =
          nodeIdMappings === undefined
            ? -1
            : layoutFilters.findIndex(
                (filter) =>
                  filter.type !== 'none' &&
                  commentPathMatchesDocId(root.path, filter, nodeIdMappings),
              );
        const layoutFilter = layoutOrder >= 0 ? layoutFilters[layoutOrder] : undefined;

        return {
          comment: toCardViewModel(root, replies),
          layoutOrder,
          documentPosition:
            (nodeIdMappings && layoutFilter && layoutFilter.type !== 'none'
              ? getNodeIdPosition(root.nodeId ?? root.path[0], layoutFilter, nodeIdMappings)
              : undefined) ?? root.startOffset,
        };
      })
      .sort((a, b) => {
        // Sort by editor order, then by the anchor's absolute position in that editor.
        // Comments that no longer match content sink to the bottom.
        const aLayout = a.layoutOrder < 0 ? Number.MAX_SAFE_INTEGER : a.layoutOrder;
        const bLayout = b.layoutOrder < 0 ? Number.MAX_SAFE_INTEGER : b.layoutOrder;
        if (aLayout !== bLayout) return aLayout - bLayout;

        if (a.documentPosition !== b.documentPosition) {
          return a.documentPosition - b.documentPosition;
        }

        return a.comment.startOffset - b.comment.startOffset;
      })
      .map(({ comment }) => comment);
  }, [
    openedComments,
    filteredComments,
    filter,
    sourceFilter,
    isPagedContent,
    nodeIdMappings,
    layoutFilters,
  ]);

  return {
    filter,
    setFilter,
    sourceFilter,
    setSourceFilter,
    visibleComments,
    draftState: workflow?.draftState ?? WorkflowState.NEW,
    reviewStatus: workflow?.publisherReviewInfo?.reviewStatus ?? PublisherReviewStatus.PENDING,
    // A failed load exits the loading state so the error+retry can show; without
    // an error we spin until both the comments and the workflow are present.
    isLoading: loadError === undefined && (openedComments === undefined || !workflow),
    loadErrorMessage: loadError?.message,
    retryLoad,
    activeThreadId,
    toggleThread,
  };
}
